import { supabase } from '../lib/supabase';

class RoleTypeService {
  // Get all role type options by category
  async getRoleTypeOptions(category = null) {
    try {
      let query = supabase?.from('role_type_options')?.select('*')?.eq('is_active', true)?.order('sort_order');

      if (category) {
        query = query?.eq('category', category);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Get voice types
  async getVoiceTypes() {
    return this.getRoleTypeOptions('voice_type');
  }

  // Get role types
  async getRoleTypes() {
    return this.getRoleTypeOptions('role_type');
  }

  // Get age ranges
  async getAgeRanges() {
    return this.getRoleTypeOptions('age_range');
  }

  // Get vocal range notes (unified single dropdown)
  async getVocalRangeNotes() {
    return this.getRoleTypeOptions('vocal_range');
  }

  // Get vocal range low notes (kept for backward compatibility)
  async getVocalRangeLow() {
    return this.getRoleTypeOptions('vocal_range');
  }

  // Get vocal range high notes (kept for backward compatibility) 
  async getVocalRangeHigh() {
    return this.getRoleTypeOptions('vocal_range');
  }

  // Admin functions - Enhanced with duplicate handling
  async createRoleTypeOption(optionData, userId) {
    try {
      // Use the safe creation function that handles duplicates
      const { data, error } = await supabase?.rpc('create_role_type_option_safe', {
        p_type_name: optionData?.type_name,
        p_category: optionData?.category,
        p_display_label: optionData?.display_label,
        p_sort_order: optionData?.sort_order || 0,
        p_is_active: optionData?.is_active !== false,
        p_user_id: userId
      });

      if (error) {
        throw error;
      }

      return { 
        data: {
          id: data?.id,
          type_name: data?.type_name,
          category: data?.category,
          display_label: data?.display_label,
          sort_order: data?.sort_order,
          is_active: data?.is_active,
          created_at: data?.created_at,
          updated_at: data?.updated_at
        }, 
        error: null,
        isDuplicate: data?.duplicate,
        message: data?.message 
      };
    } catch (err) {
      return { data: null, error: err, isDuplicate: false, message: err?.message };
    }
  }

  // Alternative method using upsert (insert or update)
  async upsertRoleTypeOption(optionData, userId) {
    try {
      const { data, error } = await supabase?.rpc('upsert_role_type_option', {
        p_type_name: optionData?.type_name,
        p_category: optionData?.category,
        p_display_label: optionData?.display_label,
        p_sort_order: optionData?.sort_order || 0,
        p_is_active: optionData?.is_active !== false,
        p_user_id: userId
      });

      if (error) {
        throw error;
      }

      // Fetch the complete record
      const { data: completeRecord, error: fetchError } = await supabase
        ?.from('role_type_options')
        ?.select('*')
        ?.eq('id', data)
        ?.single();

      if (fetchError) {
        throw fetchError;
      }

      return { data: completeRecord, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Enhanced create method with fallback for direct table insertion
  async createRoleTypeOptionWithFallback(optionData, userId) {
    try {
      // First try the safe method
      const result = await this.createRoleTypeOption(optionData, userId);
      
      if (result?.error) {
        // If safe method fails, try direct insertion with ON CONFLICT handling
        const { data, error } = await supabase?.from('role_type_options')?.insert({
          ...optionData,
          created_by: userId,
          updated_by: userId
        })?.select()?.single();

        if (error) {
          // Check if it's a unique constraint violation
          if (error?.code === '23505' && error?.message?.includes('role_type_options_type_name_key')) {
            // Find the existing record
            const { data: existingData, error: existingError } = await supabase
              ?.from('role_type_options')
              ?.select('*')
              ?.eq('type_name', optionData?.type_name)
              ?.single();

            if (existingError) {
              throw existingError;
            }

            return { 
              data: existingData, 
              error: null,
              isDuplicate: true,
              message: 'Role type option already exists with this name' 
            };
          }
          throw error;
        }

        return { data, error: null, isDuplicate: false };
      }

      return result;
    } catch (err) {
      return { data: null, error: err };
    }
  }

  async updateRoleTypeOption(id, optionData, userId) {
    try {
      const { data, error } = await supabase?.from('role_type_options')?.update({
          ...optionData,
          updated_by: userId,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  async deleteRoleTypeOption(id) {
    try {
      const { data, error } = await supabase?.from('role_type_options')?.delete()?.eq('id', id)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Get all options for admin management
  async getAllRoleTypeOptions() {
    try {
      const { data, error } = await supabase?.from('role_type_options')?.select('*')?.order('category')?.order('sort_order');

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Enhanced check if user is admin/editor with better error handling
  async isUserAdmin(userId) {
    try {
      // Method 1: Check user profile directly
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('role')
        ?.eq('id', userId)
        ?.single();

      if (!profileError && profile) {
        return { 
          data: profile?.role === 'superuser' || profile?.role === 'editor', 
          error: null,
          method: 'profile_direct'
        };
      }

      // Method 2: Use database function as fallback
      const { data: functionResult, error: functionError } = await supabase
        ?.rpc('is_editor_or_superuser');

      if (!functionError) {
        return { 
          data: functionResult, 
          error: null,
          method: 'function_check'
        };
      }

      // Method 3: Final fallback - assume not admin if all checks fail
      console.warn('All admin check methods failed:', { profileError, functionError });
      return { 
        data: false, 
        error: null,
        method: 'fallback',
        warning: 'Unable to verify admin status - defaulting to non-admin'
      };
    } catch (err) {
      console.error('Error in isUserAdmin:', err);
      return { 
        data: false, 
        error: err,
        method: 'error',
        warning: 'Error checking admin status - defaulting to non-admin'
      };
    }
  }

  // Check for duplicate type_name before creating
  async checkDuplicateTypeName(typeName) {
    try {
      const { data, error } = await supabase
        ?.from('role_type_options')
        ?.select('id, type_name, category, display_label')
        ?.eq('type_name', typeName)
        ?.single();

      if (error && error?.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        throw error;
      }

      return { 
        exists: !!data, 
        data: data || null, 
        error: null 
      };
    } catch (err) {
      return { exists: false, data: null, error: err };
    }
  }
}

export default new RoleTypeService();